import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { underwriting, products, submissions as submissionsApi, quotes, communications } from '../services/api.js';
// Chat widget removed — replaced by co-pilot analysis panel
import { getRiskTier, getRiskBadgeClass, getRiskLabel, CYBER_THRESHOLDS, GENAI_THRESHOLDS } from '../utils/riskUtils';
import './UnderwritingCanvas.css';

function getConfidenceDot(confidence) {
  if (confidence == null) return null;
  const pct = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
  let color = 'var(--color-risk-green)';
  if (pct < 0.5) color = 'var(--color-risk-red)';
  else if (pct < 0.75) color = 'var(--color-risk-orange)';
  else if (pct < 0.9) color = 'var(--color-risk-yellow)';
  return (
    <span
      className="uc__confidence-dot"
      style={{ backgroundColor: color }}
      title={`Confidence: ${Math.round(pct * 100)}%`}
    />
  );
}

function formatKey(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const CURRENCY_KEYS = /revenue|premium|limit|deductible|tiv|valuation|amount|price|cost|wage|salary|income|retention/i;

function formatValue(key, value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'number' && CURRENCY_KEYS.test(key)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

const DATA_SECTIONS = [
  {
    key: 'company',
    label: 'Company Information',
    fields: ['companyName', 'naicsCode', 'industry', 'revenue', 'employeeCount', 'state', 'yearEstablished', 'requestedLimit', 'requestedRetention', 'applicationFormat'],
  },
  {
    key: 'mfa',
    label: 'Multi-Factor Authentication',
    fields: ['mfa_remote', 'mfa_cloud', 'mfa_email', 'mfa_privileged', 'mfa_provider'],
  },
  {
    key: 'endpoint',
    label: 'Endpoint & Network Security',
    fields: ['edr_deployed', 'edr_100pct', 'edr_provider', 'ngav', 'pam', 'siem', 'soc', 'soc_24x7', 'soc_outsourced', 'vuln_scanning', 'patch_cadence', 'eol_software', 'powershell_hardened', 'macros_disabled'],
  },
  {
    key: 'email',
    label: 'Email Security',
    fields: ['email_prescreening', 'email_sandbox', 'spf', 'dkim', 'dmarc'],
  },
  {
    key: 'governance',
    label: 'Security Governance',
    fields: ['security_training', 'phishing_simulation', 'ir_plan_tested', 'dedicated_security_team', 'third_party_risk_program', 'soc2_current'],
  },
  {
    key: 'backup',
    label: 'Backup & Recovery',
    fields: ['backup_has_backups', 'backup_type', 'backup_encrypted', 'backup_immutable', 'backup_separate_credentials', 'backup_mfa_protected', 'backup_tested_6mo', 'backup_frequency', 'backup_restore_time'],
  },
  {
    key: 'exposure',
    label: 'Data & Exposure Profile',
    fields: ['pii_records_electronic', 'pii_records_paper', 'phi_data', 'pci_data', 'pci_compliant', 'biometric_data', 'wire_transfers', 'cloud_provider', 'cloud_encrypted', 'remote_access', 'byod', 'hosting_services', 'public_api', 'uptime_guarantee', 'admin_access_clients', 'ot_systems'],
  },
  {
    key: 'loss',
    label: 'Loss History',
    fields: ['prior_claims', 'prior_claims_detail', 'prior_breach_notification', 'prior_ransomware', 'prior_wire_fraud', 'prior_outage', 'prior_nonrenewal', 'knowledge_of_circumstances'],
  },
  {
    key: 'wire',
    label: 'Wire Transfer Controls',
    fields: ['documentation_form', 'written_authorization', 'separation_of_authority', 'callback_new_vendor', 'callback_account_change'],
  },
  {
    key: 'narrative',
    label: 'Narrative Responses',
    fields: ['security_measures', 'data_assets', 'contract_liability'],
  },
];

function BooleanBadge({ value }) {
  if (value === true) return <span className="uc__bool-badge uc__bool-badge--yes">Yes</span>;
  if (value === false) return <span className="uc__bool-badge uc__bool-badge--no">No</span>;
  return <span className="uc__data-value">—</span>;
}

function DataField({ fieldKey, value }) {
  if (value == null || value === '') return null;

  const label = formatKey(fieldKey);

  if (typeof value === 'boolean') {
    return (
      <div className="uc__data-row">
        <div className="uc__data-label">{label}</div>
        <div className="uc__data-value"><BooleanBadge value={value} /></div>
      </div>
    );
  }

  return (
    <div className="uc__data-row">
      <div className="uc__data-label">{label}</div>
      <div className="uc__data-value">{formatValue(fieldKey, value)}</div>
    </div>
  );
}

function StructuredDataSections({ data }) {
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    DATA_SECTIONS.forEach((s) => { initial[s.key] = true; });
    return initial;
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const usedKeys = new Set();
  const sections = DATA_SECTIONS.map((section) => {
    const fields = section.fields.filter((f) => data[f] != null && data[f] !== '');
    fields.forEach((f) => usedKeys.add(f));
    return { ...section, fields };
  }).filter((s) => s.fields.length > 0);

  const otherKeys = Object.keys(data).filter((k) => !usedKeys.has(k) && data[k] != null && data[k] !== '');

  return (
    <div className="uc__data-sections">
      {sections.map((section) => (
        <div key={section.key} className="uc__data-section">
          <button className="uc__section-header" onClick={() => toggleSection(section.key)}>
            <span>{section.label}</span>
            <span className="uc__section-count">{section.fields.length}</span>
            <span className="uc__driver-chevron">{openSections[section.key] ? '−' : '+'}</span>
          </button>
          {openSections[section.key] && (
            <div className="uc__data-list">
              {section.fields.map((fieldKey) => (
                <DataField key={fieldKey} fieldKey={fieldKey} value={data[fieldKey]} />
              ))}
            </div>
          )}
        </div>
      ))}
      {otherKeys.length > 0 && (
        <div className="uc__data-section">
          <button className="uc__section-header" onClick={() => toggleSection('other')}>
            <span>Other Fields</span>
            <span className="uc__section-count">{otherKeys.length}</span>
            <span className="uc__driver-chevron">{openSections.other ? '−' : '+'}</span>
          </button>
          {openSections.other && (
            <div className="uc__data-list">
              {otherKeys.map((fieldKey) => (
                <DataField key={fieldKey} fieldKey={fieldKey} value={data[fieldKey]} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function normalizeStructuredData(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const flat = {};

  for (const [key, value] of Object.entries(raw)) {
    if (key === 'securityControls' && typeof value === 'object' && value !== null) {
      const map = {
        MFARemote: 'mfa_remote', MFACloud: 'mfa_cloud', MFAEmail: 'mfa_email', MFAPrivileged: 'mfa_privileged',
        EDRDeployed: 'edr_deployed', EDR100Pct: 'edr_100pct', NGAV: 'ngav',
        BackupHasBackups: 'backup_has_backups', BackupImmutable: 'backup_immutable', BackupEncrypted: 'backup_encrypted',
        BackupTested6Mo: 'backup_tested_6mo', BackupFrequency: 'backup_frequency',
        EmailPrescreening: 'email_prescreening', EmailSandbox: 'email_sandbox',
        SPF: 'spf', DKIM: 'dkim', DMARC: 'dmarc',
        PatchCadence: 'patch_cadence',
        PIIRecordsElectronic: 'pii_records_electronic', PHIData: 'phi_data',
        PCIData: 'pci_data', PCICompliant: 'pci_compliant',
        WireTransfers: 'wire_transfers',
        PriorClaims: 'prior_claims', PriorRansomware: 'prior_ransomware', PriorBreachNotification: 'prior_breach_notification',
      };
      for (const [scKey, scVal] of Object.entries(value)) {
        const mapped = map[scKey] || scKey.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
        if (scVal !== null) flat[mapped] = scVal;
      }
    } else if (key === 'annualRevenue') {
      flat.revenue = value;
    } else if (key === 'requestedDeductible') {
      flat.requestedRetention = value;
    } else if (key === 'riskFactors' && Array.isArray(value)) {
      flat.riskFactors = value.join('; ');
    } else if (key === 'claimsHistory' && typeof value === 'string') {
      flat.prior_claims_detail = value;
    } else if (typeof value !== 'object' || value === null) {
      flat[key] = value;
    } else {
      flat[key] = value;
    }
  }
  return flat;
}

function autoTagFromData(data) {
  if (!data || typeof data !== 'object') return [];

  const tags = [];
  const val = (key) => data[key];
  const isTruthy = (key) => val(key) === true || val(key) === 'Yes' || val(key) === 'yes';
  const isFalsy = (key) => val(key) === false || val(key) === 'No' || val(key) === 'no';

  // MFA
  const mfaRemote = isTruthy('mfa_remote');
  const mfaCloud = isTruthy('mfa_cloud');
  const mfaEmail = isTruthy('mfa_email');
  const mfaPriv = isTruthy('mfa_privileged');
  const mfaCount = [mfaRemote, mfaCloud, mfaEmail, mfaPriv].filter(Boolean).length;

  if (mfaCount === 4) tags.push('CTR01CYB');
  else if (mfaCount > 0) tags.push('CTR02CYB');

  if (mfaCount === 0 && (isFalsy('mfa_remote') || isFalsy('mfa_cloud'))) tags.push('CTR03CYB');

  // EDR
  if (isTruthy('edr_deployed')) tags.push('CTR04CYB');
  if (isFalsy('edr_deployed') && isFalsy('ngav')) tags.push('CTR05CYB');

  // Backups
  if (isTruthy('backup_immutable')) tags.push('CTR06CYB');
  if (isTruthy('backup_has_backups') && isFalsy('backup_tested_6mo')) tags.push('CTR07CYB');

  // IR Plan
  if (isTruthy('ir_plan_tested')) tags.push('CTR08CYB');
  if (isFalsy('ir_plan_tested') && val('ir_plan_tested') != null) tags.push('CTR09CYB');

  // Vulnerability scanning
  if (isTruthy('vuln_scanning')) tags.push('CTR10CYB');

  // Patching
  if (val('patch_cadence') === '1 month or longer') tags.push('CTR11CYB');

  // PAM
  if (isTruthy('pam')) tags.push('CTR12CYB');
  if (isFalsy('pam') && isFalsy('mfa_privileged')) tags.push('CTR13CYB');

  // Email security
  if (isTruthy('email_prescreening') && (isTruthy('spf') || isTruthy('dkim') || isTruthy('dmarc'))) tags.push('CTR14CYB');

  // Security training
  if (isFalsy('security_training')) tags.push('CTR15CYB');

  // Dedicated security team
  if (isTruthy('dedicated_security_team')) tags.push('CTR16CYB');
  if (isFalsy('dedicated_security_team') && val('dedicated_security_team') != null) tags.push('CTR17CYB');

  // SOC2
  if (isTruthy('soc2_current')) tags.push('CTR18CYB');

  // Third-party risk
  if (isTruthy('third_party_risk_program')) tags.push('CTR19CYB');

  // Prior non-renewal
  if (isTruthy('prior_nonrenewal')) tags.push('CTR20CYB');

  // Exposure tags
  const piiElectronic = Number(val('pii_records_electronic')) || 0;
  if (piiElectronic > 100000) tags.push('EXP01CYB');

  if (isTruthy('phi_data')) tags.push('EXP02CYB');
  if (isTruthy('pci_data')) tags.push('EXP03CYB');
  if (isTruthy('wire_transfers')) tags.push('EXP04CYB');
  if (isTruthy('admin_access_clients')) tags.push('EXP05CYB');
  if (isTruthy('hosting_services') && isTruthy('public_api')) tags.push('EXP06CYB');
  if (isTruthy('public_api')) tags.push('EXP07CYB');
  if (isTruthy('uptime_guarantee')) tags.push('EXP08CYB');
  if (isTruthy('ot_systems')) {
    tags.push('EXP12CYB');
    // Only tag as critical infrastructure for utilities/energy/healthcare, NOT construction
    const industry = (val('industry') || '').toLowerCase();
    if (industry.includes('energy') || industry.includes('utilit') || industry.includes('healthcare') || industry.includes('water') || industry.includes('power')) {
      tags.push('EXP09CYB');
    }
  }

  if (isTruthy('hosting_services')) tags.push('EXP10CYB');

  const cloudProvider = val('cloud_provider');
  if (cloudProvider && typeof cloudProvider === 'string' && !cloudProvider.includes('Multiple') && cloudProvider !== 'None') {
    tags.push('EXP11CYB');
  }

  if (isTruthy('prior_claims') || isTruthy('prior_ransomware') || isTruthy('prior_breach_notification')) tags.push('EXP20CYB');

  return tags;
}

export function UnderwritingCanvas() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [canvas, setCanvas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Risk assessment form
  const [notes, setNotes] = useState('');
  const [expandedDrivers, setExpandedDrivers] = useState({});
  const [deciding, setDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState(null);
  const [rescoring, setRescoring] = useState(false);
  const cachedAssessmentRef = useRef(null);
  const rescoreTimersRef = useRef([]);

  const handleRescore = async () => {
    const currentAssessment = canvas?.riskAssessment || canvas?.Assessment || null;
    if (currentAssessment) {
      cachedAssessmentRef.current = currentAssessment;
    }
    setRescoring(true);
    try {
      await underwriting.rescore(submissionId);
      const delays = [3000, 6000, 10000, 16000, 22000, 30000, 38000, 45000];
      rescoreTimersRef.current = delays.map((delay, i) =>
        setTimeout(() => {
          loadCanvas(true);
          if (i === delays.length - 1) {
            setRescoring(false);
            cachedAssessmentRef.current = null;
          }
        }, delay)
      );
    } catch (err) {
      console.error('Rescore failed:', err);
      setRescoring(false);
      cachedAssessmentRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      rescoreTimersRef.current.forEach(clearTimeout);
    };
  }, []);
  const [decisionMade, setDecisionMade] = useState(null);

  // Underwriter assessment panel
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [manualScore, setManualScore] = useState(null);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [assessmentSaved, setAssessmentSaved] = useState(false);
  const [controlTagsOpen, setControlTagsOpen] = useState(true);
  const [exposureTagsOpen, setExposureTagsOpen] = useState(true);

  // AI Liability product support
  const [genaiTags, setGenaiTags] = useState([]);
  const [selectedGenaiTags, setSelectedGenaiTags] = useState([]);
  const [genaiControlTagsOpen, setGenaiControlTagsOpen] = useState(true);
  const [genaiExposureTagsOpen, setGenaiExposureTagsOpen] = useState(true);

  // Broker communication
  const [brokerMessage, setBrokerMessage] = useState('');
  const [sendingBrokerMsg, setSendingBrokerMsg] = useState(false);
  const [brokerMsgSent, setBrokerMsgSent] = useState(false);

  // Track whether user has explicitly overridden score
  const scoreOverriddenRef = useRef(false);
  // Auto-save status: null | 'saving' | 'saved' | 'unsaved'
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);

  const loadCanvas = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const result = await underwriting.getCanvas(submissionId);
      const raw = result.data || result;
      const canvasData = raw.data || raw;

      if (cachedAssessmentRef.current) {
        const newAssess = canvasData.riskAssessment || canvasData.Assessment || {};
        const newDrivers = newAssess.RiskDrivers || newAssess.riskDrivers || [];
        const cached = cachedAssessmentRef.current;
        const cachedDrivers = cached.RiskDrivers || cached.riskDrivers || [];
        if (newDrivers.length === 0 && cachedDrivers.length > 0) {
          const key = canvasData.riskAssessment ? 'riskAssessment' : 'Assessment';
          canvasData[key] = { ...canvasData[key], RiskDrivers: cachedDrivers };
        } else if (newDrivers.length > 0) {
          cachedAssessmentRef.current = null;
          setRescoring(false);
          rescoreTimersRef.current.forEach(clearTimeout);
        }
      }

      setCanvas(canvasData);

      // Load existing notes/decision from the assessment
      const assessment = canvasData.riskAssessment || canvasData.Assessment || {};
      setNotes(assessment.notes || assessment.Notes || '');

      if (assessment.Decision) {
        setDecisionMade({
          decision: assessment.Decision,
          rationale: assessment.DecisionRationale || assessment.Notes || '',
        });
      }

      // Pre-populate manual score from existing assessment and protect it from auto-calculate
      if ((assessment.RiskScore || assessment.riskScore) > 0) {
        setManualScore(assessment.RiskScore || assessment.riskScore);
        scoreOverriddenRef.current = true;
      }

      // Restore AI tags from saved assessment
      const existingGenaiTags = assessment.GenAIAppliedTags || assessment.genaiAppliedTags || [];
      if (existingGenaiTags.length > 0) {
        setSelectedGenaiTags(existingGenaiTags.map((t) => t.tagId || t.TagID));
      }

      // Fetch risk tags for the product
      const sub = canvasData.submission || canvasData.Submission || {};
      const productId = sub.ProductID || sub.productId;
      if (productId) {
        try {
          const tagResult = await products.listRiskTags(productId);
          const raw = tagResult.data || tagResult;
          const tagList = raw.data || raw.items || raw || [];
          const tags = Array.isArray(tagList) ? tagList : [];
          setAvailableTags(tags);

          // Pre-select tags: from existing assessment if saved, otherwise auto-detect from submission data
          const existingTags = assessment.AppliedTags || assessment.appliedTags || [];
          if (existingTags.length > 0) {
            setSelectedTags(existingTags.map((t) => t.tagId || t.TagID));
          } else {
            const structData = normalizeStructuredData(sub.StructuredData || sub.structuredData || {});
            const autoTags = autoTagFromData(structData);
            if (autoTags.length > 0) {
              setSelectedTags(autoTags);
            }
          }
        } catch (tagErr) {
          console.warn('Failed to load risk tags:', tagErr);
        }

        // If AI Liability data is detected, also fetch AI product tags
        const structData = normalizeStructuredData(sub.StructuredData || sub.structuredData || {});
        const hasGenAI = structData && (structData.genaiControls || structData.aiUseCase);
        const genaiProductId = 'DEMO-prod-genai';
        if ((hasGenAI || productId === genaiProductId) && productId !== genaiProductId) {
          try {
            const genaiTagResult = await products.listRiskTags(genaiProductId);
            const genaiRaw = genaiTagResult.data || genaiTagResult;
            const genaiTagList = genaiRaw.data || genaiRaw.items || genaiRaw || [];
            setGenaiTags(Array.isArray(genaiTagList) ? genaiTagList : []);
          } catch (genaiTagErr) {
            console.warn('Failed to load AI risk tags:', genaiTagErr);
          }
        } else if (productId === genaiProductId) {
          // Product is AI Liability itself — primary tags are already loaded
        }
      } else {
        console.warn('No productId found on submission — cannot load tags');
      }
    } catch (err) {
      setError(err.message || 'Failed to load underwriting canvas');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    loadCanvas();
  }, [loadCanvas]);

  // Poll while submission is in a non-terminal processing state
  const TERMINAL_STATUSES = ['agent_reviewed', 'in_review', 'referred', 'bound', 'declined', 'quoted'];
  useEffect(() => {
    if (!canvas) return;
    const sub = canvas.submission || canvas.Submission || {};
    const assess = canvas.riskAssessment || canvas.Assessment;
    const status = sub.Status || sub.status || '';
    const hasScore = assess != null && (assess.RiskScore || assess.riskScore) > 0;

    const isProcessing = !TERMINAL_STATUSES.includes(status) && !hasScore;

    if (isProcessing) {
      let pollCount = 0;
      const pollInterval = setInterval(() => {
        pollCount++;
        if (pollCount > 60) {
          clearInterval(pollInterval);
          return;
        }
        loadCanvas(true);
      }, 5000);
      return () => clearInterval(pollInterval);
    }
  }, [canvas, loadCanvas]);

  // Auto-calculate: sync manualScore to calculated value when tags change (unless user overrode)
  useEffect(() => {
    if (!scoreOverriddenRef.current && canvas) {
      setManualScore(null);
    }
  }, [selectedTags, canvas]);

  // Auto-save debounce: save 1s after tags or score change
  const autoSaveTimerRef = useRef(null);
  const prevTagsRef = useRef(selectedTags);
  const initialLoadRef = useRef(true);
  const loadCompleteTimeRef = useRef(0);

  useEffect(() => {
    // Skip auto-save on initial load and for 2s after canvas loads
    // This prevents auto-save from firing when auto-tagging sets tags on load
    if (!canvas || initialLoadRef.current) {
      initialLoadRef.current = false;
      loadCompleteTimeRef.current = Date.now();
      prevTagsRef.current = selectedTags;
      return;
    }
    if (Date.now() - loadCompleteTimeRef.current < 2000) {
      prevTagsRef.current = selectedTags;
      return;
    }
    setAutoSaveStatus('unsaved');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      doAutoSave();
    }, 1000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [selectedTags, selectedGenaiTags, manualScore, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the save payload — shared between auto-save and manual save to prevent drift
  const buildSavePayload = () => {
    const appliedTags = availableTags
      .filter((t) => selectedTags.includes(t.TagID || t.tagId))
      .map((t) => ({
        tagId: t.TagID || t.tagId,
        tagName: t.TagName || t.tagName,
        category: t.Category || t.category,
        adjustment: Math.round(t.ScoreImpact20 || t.scoreImpact20 || 0),
        reason: 'Manual selection',
      }));

    const genaiAppliedTags = genaiTags
      .filter((t) => selectedGenaiTags.includes(t.TagID || t.tagId))
      .map((t) => ({
        tagId: t.TagID || t.tagId,
        tagName: t.TagName || t.tagName,
        category: t.Category || t.category,
        adjustment: Math.round(t.ScoreImpact20 || t.scoreImpact20 || 0),
        reason: 'Manual selection',
      }));

    const assessment = canvas?.riskAssessment || canvas?.Assessment;
    const existingConfidence = assessment?.ConfidenceLevel ?? assessment?.confidenceLevel;

    return {
      riskScore: getFinalScore(),
      appliedTags,
      genaiAppliedTags,
      manualBaseScore: getBaseScore(),
      notes,
      confidenceLevel: existingConfidence != null ? existingConfidence : 0.8,
    };
  };

  const doAutoSave = async () => {
    setAutoSaveStatus('saving');
    try {
      const payload = buildSavePayload();

      const assessment = canvas?.riskAssessment || canvas?.Assessment;
      if (assessment) {
        await underwriting.updateAssessment(submissionId, payload);
      } else {
        await underwriting.saveAssessment(submissionId, payload);
      }

      // Update submission status from "new" to "in_review" on first save
      const sub = canvas?.submission || canvas?.Submission || {};
      const currentStatus = sub.Status || sub.status || '';
      if (currentStatus === 'new') {
        try {
          await submissionsApi.update(submissionId, { status: 'in_review' });
        } catch { /* best effort */ }
      }

      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('unsaved');
    }
  };

  // Navigation guard: warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (autoSaveStatus === 'unsaved') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [autoSaveStatus]);

  // Navigation: next/prev submission
  const goToNextSubmission = async () => {
    try {
      const result = await submissionsApi.list({ status: 'new' });
      const raw = result.data || result;
      const list = raw.data || raw.items || raw || [];
      const items = Array.isArray(list) ? list : [];
      const next = items.find(
        (s) => (s.SubmissionID || s.submissionId || s.id) !== submissionId
      );
      if (next) {
        navigate(`/underwriting/${next.SubmissionID || next.submissionId || next.id}`);
      } else {
        navigate('/');
      }
    } catch {
      navigate('/');
    }
  };

  const goToPrevSubmission = async () => {
    try {
      const result = await submissionsApi.list();
      const raw = result.data || result;
      const list = raw.data || raw.items || raw || [];
      const items = Array.isArray(list) ? list : [];
      const ids = items.map((s) => s.SubmissionID || s.submissionId || s.id);
      const currentIdx = ids.indexOf(submissionId);
      if (currentIdx > 0) {
        navigate(`/underwriting/${ids[currentIdx - 1]}`);
      }
    } catch {
      // no-op
    }
  };

  const goToNextInQueue = async () => {
    try {
      const result = await submissionsApi.list();
      const raw = result.data || result;
      const list = raw.data || raw.items || raw || [];
      const items = Array.isArray(list) ? list : [];
      const ids = items.map((s) => s.SubmissionID || s.submissionId || s.id);
      const currentIdx = ids.indexOf(submissionId);
      if (currentIdx >= 0 && currentIdx < ids.length - 1) {
        navigate(`/underwriting/${ids[currentIdx + 1]}`);
      }
    } catch {
      // no-op
    }
  };

  const toggleDriver = (idx) => {
    setExpandedDrivers((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleDecision = async (decision) => {
    setDeciding(true);
    setDecisionError(null);
    try {
      const result = await underwriting.makeDecision(submissionId, {
        decision,
        rationale: notes,
      });
      const data = result.data || result;
      setDecisionMade({ decision, rationale: notes, quoteId: data.quoteId });

      const pricing = canvas?.riskAssessment?.Pricing || canvas?.riskAssessment?.pricing ||
                       canvas?.Assessment?.Pricing || canvas?.Assessment?.pricing || {};
      const limitStr = pricing.suggestedLimit > 0 ? formatCurrency(pricing.suggestedLimit) : '';
      const premiumStr = pricing.suggestedPremium > 0 ? formatCurrency(pricing.suggestedPremium) : '';
      const flags = Array.isArray(canvas?.submission?.MissingData || canvas?.Submission?.missingData)
        ? (canvas.submission?.MissingData || canvas.Submission?.missingData) : [];
      const flagNames = flags.map(f => typeof f === 'string' ? f : f.Field || f.FieldName || f.Name || '').filter(Boolean);

      if (decision === 'quote' || decision === 'bind') {
        setBrokerMessage(
          `Based on initial review, we can offer a ${limitStr || 'competitive'} limit${premiumStr ? ` at ${premiumStr}` : ''}. See attached quote for full terms and conditions.`
        );
      } else if (decision === 'refer') {
        setBrokerMessage(
          `We need additional information on the following items before proceeding:\n\n${flagNames.length > 0 ? flagNames.map(f => `- ${f}`).join('\n') : '- [Please specify required items]'}`
        );
      } else if (decision === 'decline') {
        setBrokerMessage(
          'After thorough review, we are unable to offer coverage at this time. Please feel free to reach out with any questions.'
        );
      }
    } catch (err) {
      setDecisionError(err.message || 'Decision failed');
    } finally {
      setDeciding(false);
    }
  };

  const handleSendBrokerMessage = async () => {
    setSendingBrokerMsg(true);
    try {
      await communications.send({
        submissionId,
        accountId: canvas?.account?.AccountID || canvas?.submission?.AccountID,
        brokerId: canvas?.submission?.BrokerID || canvas?.Submission?.brokerId,
        message: brokerMessage,
        commType: existingDecision?.decision || 'general',
      });
      setBrokerMsgSent(true);
    } catch {
      setBrokerMsgSent(false);
    } finally {
      setSendingBrokerMsg(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await underwriting.updateAssessment(submissionId, { notes });
    } catch {
      // Silent save
    }
  };

  const toggleTag = (tagId) => {
    if (Date.now() - loadCompleteTimeRef.current > 3000) {
      scoreOverriddenRef.current = false;
    }
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
    setAssessmentSaved(false);
  };

  const toggleGenaiTag = (tagId) => {
    setSelectedGenaiTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
    setAssessmentSaved(false);
  };

  const getBaseScore = () => {
    const assessment = canvas?.riskAssessment || canvas?.Assessment || {};
    return assessment.ManualBaseScore || assessment.RiskScore || 0;
  };

  const getTagAdjustment = () => {
    return availableTags
      .filter((t) => selectedTags.includes(t.TagID || t.tagId))
      .reduce((sum, t) => sum + (t.ScoreImpact20 || t.scoreImpact20 || 0), 0);
  };

  const getCalculatedScore = () => {
    const base = getBaseScore();
    const adj = getTagAdjustment();
    return Math.max(1, Math.min(20, Math.round(base + adj)));
  };

  const getFinalScore = () => {
    return manualScore != null ? manualScore : getCalculatedScore();
  };

  const handleSaveAssessment = async () => {
    setSavingAssessment(true);
    setAssessmentSaved(false);
    try {
      const payload = buildSavePayload();

      const assessment = canvas?.riskAssessment || canvas?.Assessment;
      if (assessment) {
        await underwriting.updateAssessment(submissionId, payload);
      } else {
        await underwriting.saveAssessment(submissionId, payload);
      }
      setAssessmentSaved(true);
      setAutoSaveStatus('saved');
      setTimeout(() => setAssessmentSaved(false), 3000);
    } catch (err) {
      setDecisionError(err.message || 'Failed to save assessment');
    } finally {
      setSavingAssessment(false);
    }
  };

  if (loading) {
    return (
      <div className="uc-loading">
        <div className="spinner" />
        <p>Loading underwriting canvas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="uc-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadCanvas}>
          Retry
        </button>
      </div>
    );
  }

  if (!canvas) return null;

  // Extract entities – API returns lowercase keys for nested objects
  const sub = canvas.submission || canvas.Submission || {};
  const account = canvas.account || canvas.Account || {};
  const assessment = canvas.riskAssessment || canvas.Assessment || null;
  const product = canvas.product || canvas.Product || {};
  const insured = canvas.insured || canvas.Insured || {};

  // Top-level display names (handle both PascalCase and camelCase from API)
  const insuredName = canvas.InsuredName || insured.Name || insured.name || account.InsuredName || account.insuredName || sub.InsuredName || 'Submission';
  const productName = canvas.ProductName || product.Name || product.name || sub.ProductID || sub.productId || '—';
  const brokerName = canvas.BrokerName || sub.BrokerID || sub.brokerId || '—';
  const assignedUWName = canvas.AssignedUWName || sub.AssignedUW || sub.assignedUW || '—';
  const uwGuidelines = canvas.underwritingGuidelines || product.UnderwritingGuidelines || product.underwritingGuidelines || '';

  const structuredData = normalizeStructuredData(sub.StructuredData || sub.structuredData || {});
  const riskDrivers = assessment && Array.isArray(assessment.RiskDrivers || assessment.riskDrivers) ? (assessment.RiskDrivers || assessment.riskDrivers) : [];
  const disqualifiers = assessment && Array.isArray(assessment.Disqualifiers || assessment.disqualifiers) ? (assessment.Disqualifiers || assessment.disqualifiers) : [];
  const missingFlags = Array.isArray(sub.MissingData || sub.missingData) ? (sub.MissingData || sub.missingData) : [];
  const riskScore = assessment?.RiskScore ?? assessment?.riskScore ?? sub.RiskScore ?? sub.riskScore;
  const riskTier = assessment?.RiskTier || assessment?.riskTier || sub.RiskTier || sub.riskTier;
  const hasAssessment = assessment != null && (riskScore != null || riskDrivers.length > 0);
  const existingDecision = decisionMade || (assessment?.Decision || assessment?.decision ? { decision: assessment.Decision || assessment.decision, rationale: assessment.DecisionRationale || assessment.decisionRationale } : null);
  const subStatus = sub.Status || sub.status || '';
  const isScoringInProgress = subStatus === 'data_extracted';

  // Multi-product detection
  const productId = sub.ProductID || sub.productId || '';
  const isCyberProduct = productId === 'DEMO-prod-cyber' || (!productId.includes('genai'));
  const isGenAIProduct = productId === 'DEMO-prod-genai';
  const hasGenAIData = isGenAIProduct || !!(structuredData && (structuredData.genaiControls || structuredData.aiUseCase));
  const hasCyberData = isCyberProduct || !isGenAIProduct;

  const cyberScore = hasCyberData ? riskScore : null;
  const genaiScore = hasGenAIData && structuredData?.genaiControls
    ? (assessment?.GenAIRiskScore ?? assessment?.genaiRiskScore ?? riskScore)
    : (isGenAIProduct ? riskScore : null);

  return (
    <div className="uc">
      {/* Top bar */}
      <div className="uc__topbar">
        <div className="uc__topbar-left">
          <button
            className="sd__back"
            onClick={() => navigate('/')}
          >
            &larr; Back to Queue
          </button>
          <button className="uc__nav-btn" onClick={goToPrevSubmission} title="Previous submission">&larr; Prev</button>
          <button className="uc__nav-btn" onClick={goToNextInQueue} title="Next submission">Next &rarr;</button>
          <h2 className="uc__topbar-insured">{insuredName}</h2>
          <span className={getRiskBadgeClass(riskTier || getFinalScore())}>
            {getFinalScore() > 0
              ? `${getFinalScore()} · ${getRiskLabel(riskTier || getFinalScore())}`
              : riskScore != null
                ? `${riskScore} · ${getRiskLabel(riskTier || riskScore)}`
                : 'Pending'}
          </span>
        </div>
        <div className="uc__topbar-right">
          <span className="uc__topbar-meta">{productName}</span>
          <span className="uc__topbar-meta">Broker: {brokerName}</span>
          {assignedUWName && assignedUWName !== '—' ? (
            <span className="uc__uw-chip">
              <span className="uc__uw-avatar">
                {assignedUWName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
              {assignedUWName}
            </span>
          ) : (
            <Link to={`/submissions/${submissionId}`} className="uc__uw-chip uc__uw-chip--unassigned">
              <span className="uc__uw-avatar" style={{ background: 'var(--color-text-muted)' }}>?</span>
              Unassigned
            </Link>
          )}
          {(account.AccountID || sub.AccountID) && (
            <Link
              to={`/accounts/${account.AccountID || sub.AccountID}`}
              className="uc__topbar-link"
            >
              View Account
            </Link>
          )}
        </div>
      </div>

      {/* Three-panel layout */}
      <div className="uc__panels">
        {/* Left panel: Structured data + Insured details */}
        <div className="uc__panel uc__panel--left">
          <h3 className="uc__panel-title">Submission Data</h3>

          {/* Insured details */}
          {(insured.Name || insured.name || insured.Industry || insured.industry || insured.Revenue != null || insured.revenue != null) && (
            <div className="uc__insured-section">
              <h4 className="uc__sub-title">Insured Details</h4>
              {(insured.Industry || insured.industry) && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Industry</div>
                  <div className="uc__data-value">{insured.Industry || insured.industry}</div>
                </div>
              )}
              {(insured.Revenue != null || insured.revenue != null) && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Revenue</div>
                  <div className="uc__data-value">{formatCurrency(insured.Revenue ?? insured.revenue)}</div>
                </div>
              )}
              {(insured.EmployeeCount != null || insured.employeeCount != null) && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Employees</div>
                  <div className="uc__data-value">{(insured.EmployeeCount ?? insured.employeeCount ?? 0).toLocaleString()}</div>
                </div>
              )}
              {(insured.Website || insured.website) && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Website</div>
                  <div className="uc__data-value">{insured.Website || insured.website}</div>
                </div>
              )}
            </div>
          )}

          {/* Structured data from submission - grouped into sections */}
          {structuredData && Object.keys(structuredData).length > 0 ? (
            <StructuredDataSections data={structuredData} />
          ) : (
            <p className="uc__empty">No structured data available.</p>
          )}
        </div>

        {/* Center panel: Risk assessment */}
        <div className="uc__panel uc__panel--center">
          <h3 className="uc__panel-title">Risk Assessment</h3>

          {hasAssessment ? (
            <>
              {/* Risk score display — multi-product side by side */}
              <div className="uc__score-display" style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
                {hasCyberData && (
                  <div className="uc__score-product">
                    <div className="uc__score-product-label">Cyber</div>
                    <div
                      className="uc__score-circle"
                      data-tier={
                        riskTier ||
                        (cyberScore == null ? 'none' : getRiskTier(cyberScore, CYBER_THRESHOLDS))
                      }
                    >
                      <span className="uc__score-value">{cyberScore ?? '—'}</span>
                      <span className="uc__score-label">
                        {getRiskLabel(riskTier || cyberScore, CYBER_THRESHOLDS)}
                      </span>
                    </div>
                  </div>
                )}
                {hasGenAIData && (
                  <div className="uc__score-product">
                    <div className="uc__score-product-label">AI</div>
                    <div
                      className="uc__score-circle"
                      data-tier={genaiScore == null ? 'none' : getRiskTier(genaiScore, GENAI_THRESHOLDS)}
                    >
                      <span className="uc__score-value">{genaiScore ?? '—'}</span>
                      <span className="uc__score-label">
                        {getRiskLabel(genaiScore, GENAI_THRESHOLDS)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Disqualifiers - red flagged, shown prominently */}
              {disqualifiers.length > 0 && (
                <div className="uc__disqualifiers">
                  <h4 className="uc__sub-title uc__sub-title--red">Disqualifiers</h4>
                  <ul className="uc__flag-list">
                    {disqualifiers.map((d, idx) => (
                      <li key={idx} className="uc__flag-item uc__flag-item--red">
                        <span className="uc__flag-icon uc__flag-icon--red">✕</span>
                        <span>{typeof d === 'string' ? d : d.Name || d.Reason || JSON.stringify(d)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Decision rationale from AI assessment (only if no human decision yet) */}
              {assessment.DecisionRationale && !existingDecision && (
                <div className="uc__rationale-block">
                  <h4 className="uc__sub-title">AI Rationale</h4>
                  <p className="uc__rationale-text">{assessment.DecisionRationale}</p>
                </div>
              )}

              {/* Risk drivers accordion */}
              <div className={`uc__drivers ${rescoring ? 'uc__drivers--rescoring' : ''}`}>
                <h4 className="uc__sub-title">
                  Risk Drivers
                  {rescoring && <span className="uc__rescoring-badge">Re-scoring...</span>}
                </h4>
                {riskDrivers.length > 0 ? (
                  riskDrivers.map((driver, idx) => (
                    <div key={idx} className="uc__driver">
                      <button
                        className={`uc__driver-header ${expandedDrivers[idx] ? 'uc__driver-header--open' : ''}`}
                        onClick={() => toggleDriver(idx)}
                      >
                        <span className="uc__driver-name">
                          {getConfidenceDot(driver.Confidence ?? driver.confidence)}
                          {driver.Name || driver.name || `Driver ${idx + 1}`}
                        </span>
                        <span className="uc__driver-impact">
                          {driver.Impact || driver.impact || ''}
                          {(driver.Score ?? driver.score) != null ? ` (${driver.Score ?? driver.score})` : ''}
                        </span>
                        <span className="uc__driver-chevron">
                          {expandedDrivers[idx] ? '−' : '+'}
                        </span>
                      </button>
                      {expandedDrivers[idx] && (
                        <div className="uc__driver-detail">
                          <p>
                            {driver.Detail || driver.detail || 'No additional detail available.'}
                          </p>
                          {(driver.Confidence ?? driver.confidence) != null && (
                            <span className="uc__driver-confidence">
                              Confidence:{' '}
                              {Math.round(
                                (typeof (driver.Confidence ?? driver.confidence) === 'number'
                                  ? (driver.Confidence ?? driver.confidence)
                                  : parseFloat(driver.Confidence ?? driver.confidence)) * 100
                              )}
                              %
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="uc__empty">No risk drivers identified yet.</p>
                )}
              </div>
            </>
          ) : (
            <div className="uc__no-assessment">
              {isScoringInProgress ? (
                <>
                  <div className="uc__no-assessment-icon">&#9203;</div>
                  <p className="uc__no-assessment-text">AI risk scoring in progress...</p>
                  <p className="uc__no-assessment-hint">The risk assessment will appear here once processing is complete.</p>
                </>
              ) : (
                <>
                  <div className="uc__no-assessment-icon">&#128203;</div>
                  <p className="uc__no-assessment-text">Awaiting underwriter review</p>
                  <p className="uc__no-assessment-hint">Use the assessment panel below to select tags, score this risk, and record your decision.</p>
                </>
              )}
            </div>
          )}

          {/* Underwriter Assessment Panel - always visible for manual review */}
          {!existingDecision && (
            <div className="uc__assessment-panel">
              <h4 className="uc__sub-title">Underwriter Assessment</h4>

              {/* Tag selection — product-specific groups */}
              {availableTags.length === 0 && genaiTags.length === 0 && (
                <p className="uc__empty" style={{ fontSize: '0.85rem', margin: '8px 0' }}>
                  Loading risk tags...
                </p>
              )}
              {(() => {
                const cyberControlTags = availableTags.filter(
                  (t) => (t.Category || t.category || '').toLowerCase() === 'control'
                );
                const cyberExposureTags = availableTags.filter(
                  (t) => (t.Category || t.category || '').toLowerCase() === 'exposure'
                );
                const genaiControlTags = genaiTags.filter(
                  (t) => (t.Category || t.category || '').toLowerCase() === 'control'
                );
                const genaiExposureTags = genaiTags.filter(
                  (t) => (t.Category || t.category || '').toLowerCase() === 'exposure'
                );
                const cyberLabel = hasGenAIData ? 'Cyber ' : '';
                const showGenAISection = hasGenAIData && genaiTags.length > 0;
                return (
                  <>
                    {cyberControlTags.length > 0 && (
                      <div className="uc__tag-group">
                        <button
                          className="uc__tag-group-header"
                          onClick={() => setControlTagsOpen((o) => !o)}
                        >
                          <span>{cyberLabel}Control Tags ({cyberControlTags.length})</span>
                          <span className="uc__driver-chevron">{controlTagsOpen ? '−' : '+'}</span>
                        </button>
                        {controlTagsOpen && (
                          <div className="uc__tag-list">
                            {cyberControlTags.map((tag) => {
                              const id = tag.TagID || tag.tagId;
                              const impact = Math.round(tag.ScoreImpact20 || tag.scoreImpact20 || 0);
                              const checked = selectedTags.includes(id);
                              return (
                                <label key={id} className={`uc__tag-row ${checked ? 'uc__tag-row--selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleTag(id)}
                                    className="uc__tag-checkbox"
                                  />
                                  <span className="uc__tag-name">{tag.TagName || tag.tagName}</span>
                                  <span className={`uc__tag-impact ${impact <= 0 ? 'uc__tag-impact--neg' : 'uc__tag-impact--pos'}`}>
                                    {impact > 0 ? `+${impact}` : impact}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {cyberExposureTags.length > 0 && (
                      <div className="uc__tag-group">
                        <button
                          className="uc__tag-group-header"
                          onClick={() => setExposureTagsOpen((o) => !o)}
                        >
                          <span>{cyberLabel}Exposure Tags ({cyberExposureTags.length})</span>
                          <span className="uc__driver-chevron">{exposureTagsOpen ? '−' : '+'}</span>
                        </button>
                        {exposureTagsOpen && (
                          <div className="uc__tag-list">
                            {cyberExposureTags.map((tag) => {
                              const id = tag.TagID || tag.tagId;
                              const impact = Math.round(tag.ScoreImpact20 || tag.scoreImpact20 || 0);
                              const checked = selectedTags.includes(id);
                              return (
                                <label key={id} className={`uc__tag-row ${checked ? 'uc__tag-row--selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleTag(id)}
                                    className="uc__tag-checkbox"
                                  />
                                  <span className="uc__tag-name">{tag.TagName || tag.tagName}</span>
                                  <span className={`uc__tag-impact ${impact <= 0 ? 'uc__tag-impact--neg' : 'uc__tag-impact--pos'}`}>
                                    {impact > 0 ? `+${impact}` : impact}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {showGenAISection && genaiControlTags.length > 0 && (
                      <div className="uc__tag-group">
                        <button
                          className="uc__tag-group-header"
                          onClick={() => setGenaiControlTagsOpen((o) => !o)}
                        >
                          <span>AI Control Tags ({genaiControlTags.length})</span>
                          <span className="uc__driver-chevron">{genaiControlTagsOpen ? '−' : '+'}</span>
                        </button>
                        {genaiControlTagsOpen && (
                          <div className="uc__tag-list">
                            {genaiControlTags.map((tag) => {
                              const id = tag.TagID || tag.tagId;
                              const impact = Math.round(tag.ScoreImpact20 || tag.scoreImpact20 || 0);
                              const checked = selectedGenaiTags.includes(id);
                              return (
                                <label key={id} className={`uc__tag-row ${checked ? 'uc__tag-row--selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleGenaiTag(id)}
                                    className="uc__tag-checkbox"
                                  />
                                  <span className="uc__tag-name">{tag.TagName || tag.tagName}</span>
                                  <span className={`uc__tag-impact ${impact <= 0 ? 'uc__tag-impact--neg' : 'uc__tag-impact--pos'}`}>
                                    {impact > 0 ? `+${impact}` : impact}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {showGenAISection && genaiExposureTags.length > 0 && (
                      <div className="uc__tag-group">
                        <button
                          className="uc__tag-group-header"
                          onClick={() => setGenaiExposureTagsOpen((o) => !o)}
                        >
                          <span>AI Exposure Tags ({genaiExposureTags.length})</span>
                          <span className="uc__driver-chevron">{genaiExposureTagsOpen ? '−' : '+'}</span>
                        </button>
                        {genaiExposureTagsOpen && (
                          <div className="uc__tag-list">
                            {genaiExposureTags.map((tag) => {
                              const id = tag.TagID || tag.tagId;
                              const impact = Math.round(tag.ScoreImpact20 || tag.scoreImpact20 || 0);
                              const checked = selectedGenaiTags.includes(id);
                              return (
                                <label key={id} className={`uc__tag-row ${checked ? 'uc__tag-row--selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleGenaiTag(id)}
                                    className="uc__tag-checkbox"
                                  />
                                  <span className="uc__tag-name">{tag.TagName || tag.tagName}</span>
                                  <span className={`uc__tag-impact ${impact <= 0 ? 'uc__tag-impact--neg' : 'uc__tag-impact--pos'}`}>
                                    {impact > 0 ? `+${impact}` : impact}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Score calculation */}
              <div className="uc__score-calc">
                <div className="uc__score-calc-row">
                  <span>Base Score</span>
                  <span className="uc__score-calc-val">{getBaseScore()}</span>
                </div>
                <div className="uc__score-calc-row">
                  <span>Tag Adjustments</span>
                  <span className={`uc__score-calc-val ${getTagAdjustment() > 0 ? 'uc__tag-impact--pos' : getTagAdjustment() < 0 ? 'uc__tag-impact--neg' : ''}`}>
                    {getTagAdjustment() > 0 ? `+${Math.round(getTagAdjustment())}` : Math.round(getTagAdjustment())}
                  </span>
                </div>
                <div className="uc__score-calc-row uc__score-calc-row--total">
                  <span>Calculated Score</span>
                  <span className="uc__score-calc-val">{getCalculatedScore()}</span>
                </div>
                <div className="uc__score-calc-row">
                  <label className="uc__final-score-label">
                    Final Score (1–20)
                    <div className="uc__final-score-input-wrap">
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={manualScore != null ? manualScore : getCalculatedScore()}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          scoreOverriddenRef.current = true;
                          setManualScore(isNaN(v) ? null : Math.max(1, Math.min(20, v)));
                          setAssessmentSaved(false);
                        }}
                        className="uc__final-score-input"
                      />
                      <span
                        className="uc__score-tier-dot"
                        data-tier={getRiskTier(getFinalScore())}
                        title={getRiskLabel(getFinalScore())}
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Save button + auto-save status */}
              <div className="uc__save-row">
                <button
                  className="btn btn--secondary uc__btn-save-assessment"
                  onClick={handleSaveAssessment}
                  disabled={savingAssessment}
                >
                  {savingAssessment ? 'Saving...' : assessmentSaved ? 'Saved!' : 'Save Assessment'}
                </button>
                {autoSaveStatus && (
                  <span className={`uc__autosave-status uc__autosave-status--${autoSaveStatus}`}>
                    {autoSaveStatus === 'saving' && 'Saving...'}
                    {autoSaveStatus === 'saved' && 'Auto-saved'}
                    {autoSaveStatus === 'unsaved' && 'Unsaved changes'}
                  </span>
                )}
              </div>

              {/* Pricing Indication */}
              {(() => {
                const pricing = assessment?.pricing || assessment?.Pricing;
                if (!pricing) return null;
                return (
                  <div className="uc__pricing-section">
                    <h4 className="uc__sub-title">Pricing Indication</h4>
                    <div className="uc__pricing-premium">
                      {formatCurrency(pricing.suggestedPremium)}
                    </div>
                    {(pricing.premiumRangeLow != null || pricing.premiumRangeHigh != null) && (
                      <div className="uc__pricing-range">
                        Range: {formatCurrency(pricing.premiumRangeLow)} &ndash; {formatCurrency(pricing.premiumRangeHigh)}
                      </div>
                    )}
                    <div className="uc__pricing-details">
                      {pricing.suggestedRetention != null && (
                        <div className="uc__data-row">
                          <div className="uc__data-label">Retention</div>
                          <div className="uc__data-value">{formatCurrency(pricing.suggestedRetention)}</div>
                        </div>
                      )}
                      {pricing.suggestedLimit != null && (
                        <div className="uc__data-row">
                          <div className="uc__data-label">Limit</div>
                          <div className="uc__data-value">{formatCurrency(pricing.suggestedLimit)}</div>
                        </div>
                      )}
                    </div>
                    {pricing.ransomwareSublimit && (
                      <div className="uc__pricing-sublimit">Ransomware Sublimit Recommended</div>
                    )}
                    {pricing.pricingRationale && (
                      <p className="uc__pricing-rationale">{pricing.pricingRationale}</p>
                    )}
                    {pricing.comparableAccount && (
                      <p className="uc__pricing-comparable">Based on: {pricing.comparableAccount}</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Decision success message */}
          {existingDecision && (
            <div className={`uc__decision-result uc__decision-result--${existingDecision.decision}`}>
              <h4 className="uc__decision-result-title">
                Decision: <span style={{ textTransform: 'capitalize' }}>{existingDecision.decision}</span>
              </h4>
              {existingDecision.rationale && (
                <p className="uc__decision-result-rationale">{existingDecision.rationale}</p>
              )}
              {(existingDecision.decision === 'bind' || existingDecision.decision === 'quote') && existingDecision.quoteId && (
                <div style={{ marginTop: 'var(--space-sm)' }}>
                  <p style={{ fontSize: '13px', marginBottom: 'var(--space-xs)' }}>
                    Draft quote created automatically.
                  </p>
                  <button
                    className="btn btn--primary"
                    onClick={async () => {
                      try {
                        await quotes.deliver(existingDecision.quoteId);
                        alert('Quote delivered to broker.');
                      } catch (err) {
                        alert('Failed to deliver quote: ' + (err.message || err));
                      }
                    }}
                  >
                    Deliver Quote to Broker
                  </button>
                </div>
              )}

              {/* Broker Communication */}
              <div className="uc__broker-comm">
                <h4 className="uc__broker-comm-title">Broker Communication</h4>
                {brokerMsgSent ? (
                  <p className="uc__broker-comm-sent">Message sent to broker.</p>
                ) : (
                  <>
                    <textarea
                      className="uc__broker-comm-textarea"
                      value={brokerMessage}
                      onChange={(e) => setBrokerMessage(e.target.value)}
                      placeholder="Enter message to broker..."
                      rows={4}
                    />
                    <div className="uc__broker-comm-actions">
                      <button
                        className="btn btn--primary"
                        onClick={handleSendBrokerMessage}
                        disabled={sendingBrokerMsg || !brokerMessage.trim()}
                      >
                        {sendingBrokerMsg ? 'Sending...' : 'Send to Broker'}
                      </button>
                    </div>
                  </>
                )}
              </div>

              <button className="btn btn--primary uc__btn-next-submission" onClick={goToNextSubmission} style={{ marginTop: 'var(--space-sm)' }}>
                Next Submission &rarr;
              </button>
            </div>
          )}

          {/* Notes + decision buttons */}
          {!existingDecision && (
            <>
              <div className="uc__notes">
                <h4 className="uc__sub-title">Underwriter Notes</h4>
                <textarea
                  className="uc__notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={handleSaveNotes}
                  placeholder="Add rationale notes before making a decision..."
                  rows={5}
                />
              </div>

              {decisionError && (
                <div className="uc__decision-error">{decisionError}</div>
              )}
              <div className="uc__decision-actions">
                <button
                  className="btn uc__btn-quote"
                  onClick={() => handleDecision('quote')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Quote'}
                </button>
                <button
                  className="btn uc__btn-bind"
                  onClick={() => handleDecision('bind')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Bind'}
                </button>
                <button
                  className="btn uc__btn-refer"
                  onClick={() => handleDecision('refer')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Refer'}
                </button>
                <button
                  className="btn btn--danger"
                  onClick={() => handleDecision('decline')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Decline'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right panel: DEX Engine Co-Pilot */}
        <div className="uc__panel uc__panel--right">

          {/* AI Analysis Header */}
          <div className="uc__copilot-header">
            <div>
              <h3 className="uc__panel-title" style={{ margin: 0 }}>DEX Engine Analysis</h3>
            </div>
            <button
              className="uc__rerun-btn"
              onClick={handleRescore}
              disabled={rescoring}
              title="Re-run AI risk scoring and agent analysis"
            >
              {rescoring ? '...' : '\u21BB'} {rescoring ? 'Analyzing' : 'Rerun'}
            </button>
          </div>

          {/* Pricing — elevated to top for actionability */}
          {(assessment?.Pricing || assessment?.pricing) && (() => {
            const p = assessment.Pricing || assessment.pricing;
            const isHighHazard = (riskTier === 'red') || (riskScore >= 14);
            return (
              <div className="uc__copilot-section">
                <h4 className="uc__copilot-label">
                  {isHighHazard ? 'Pricing Indication' : 'Bindable Quote Pricing'}
                </h4>
                <div className="uc__copilot-pricing">
                  {p.suggestedPremium > 0 && (
                    <div className="uc__copilot-pricing-row">
                      <span>Suggested Premium</span>
                      <strong>{formatCurrency(p.suggestedPremium)}</strong>
                    </div>
                  )}
                  {(p.premiumRangeLow > 0 || p.premiumRangeHigh > 0) && (
                    <div className="uc__copilot-pricing-row">
                      <span>Premium Range</span>
                      <span>{formatCurrency(p.premiumRangeLow)} – {formatCurrency(p.premiumRangeHigh)}</span>
                    </div>
                  )}
                  {p.suggestedLimit > 0 && (
                    <div className="uc__copilot-pricing-row">
                      <span>Limit</span>
                      <span>{formatCurrency(p.suggestedLimit)}</span>
                    </div>
                  )}
                  {p.suggestedRetention > 0 && (
                    <div className="uc__copilot-pricing-row">
                      <span>Retention</span>
                      <span>{formatCurrency(p.suggestedRetention)}</span>
                    </div>
                  )}
                  {p.ransomwareSublimit && (
                    <div className="uc__copilot-pricing-row uc__copilot-pricing-row--warn">
                      <span>Ransomware Sublimit</span>
                      <span>Recommended</span>
                    </div>
                  )}
                </div>
                {p.pricingRationale && (
                  <p className="uc__copilot-text uc__copilot-text--sm">{p.pricingRationale}</p>
                )}
              </div>
            );
          })()}

          {/* AI Summary / Rationale */}
          {(assessment?.AgentRationale || assessment?.agentRationale || assessment?.Notes || assessment?.notes) && (
            <div className="uc__copilot-section">
              <h4 className="uc__copilot-label">Analysis Summary</h4>
              <p className="uc__copilot-text">
                {assessment.AgentRationale || assessment.agentRationale || assessment.Notes || assessment.notes}
              </p>
            </div>
          )}

          {/* Recommendation */}
          {!existingDecision && (assessment?.decisionRecommendation || assessment?.DecisionRecommendation) && (
            <div className="uc__copilot-section">
              <h4 className="uc__copilot-label">Recommendation</h4>
              <div className={`uc__copilot-recommendation uc__copilot-recommendation--${(assessment.decisionRecommendation || assessment.DecisionRecommendation || '').toLowerCase()}`}>
                <span className="uc__copilot-recommendation-decision">
                  {(assessment.decisionRecommendation || assessment.DecisionRecommendation || '').toUpperCase()}
                </span>
                <button
                  className="btn btn--sm btn--primary"
                  onClick={() => {
                    const rec = assessment.decisionRecommendation || assessment.DecisionRecommendation;
                    setNotes(assessment.agentRationale || assessment.AgentRationale || '');
                    handleDecision(rec);
                  }}
                  disabled={deciding}
                >
                  {deciding ? '...' : 'Approve'}
                </button>
              </div>
            </div>
          )}

          {/* AI-driven quote CTA */}
          {!existingDecision && (assessment?.Pricing || assessment?.pricing) && (() => {
            const p = assessment.Pricing || assessment.pricing;
            const tier = riskTier || getRiskTier(riskScore);
            const isLowRisk = tier === 'green' || tier === 'yellow';
            const rec = (assessment?.decisionRecommendation || assessment?.DecisionRecommendation || '').toLowerCase();
            if (!isLowRisk && rec !== 'bind') return null;
            const limitStr = p.suggestedLimit > 0 ? formatCurrency(p.suggestedLimit) : '$2M';
            return (
              <div className="uc__copilot-section uc__ai-cta">
                <p className="uc__ai-cta-text">
                  Low hazard score. Application looks strong. Would you like to generate a {limitStr} quote?
                </p>
                <button
                  className="btn btn--primary uc__ai-cta-btn"
                  onClick={() => handleDecision('quote')}
                  disabled={deciding}
                >
                  {deciding ? 'Generating...' : `Generate ${limitStr} Quote`}
                </button>
              </div>
            );
          })()}

          {/* Missing data flags — collapsible */}
          {missingFlags.length > 0 ? (
            <details className="uc__copilot-section">
              <summary className="uc__copilot-label uc__missing-summary">
                Missing Data
                <span className="uc__missing-badge">{missingFlags.length}</span>
              </summary>
              <ul className="uc__flag-list" style={{ marginTop: 'var(--space-xs)' }}>
                {missingFlags.map((flag, idx) => (
                  <li key={idx} className="uc__flag-item">
                    <span className="uc__flag-icon">!</span>
                    <span>
                      {typeof flag === 'string'
                        ? flag
                        : flag.Field || flag.FieldName || flag.Name || JSON.stringify(flag)}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : (
            <div className="uc__copilot-section">
              <span className="uc__copilot-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: 'var(--color-risk-green)' }}>&#10003;</span> All Data Captured
              </span>
            </div>
          )}

          {/* Underwriting Guidelines — collapsed by default */}
          {uwGuidelines && (
            <details className="uc__copilot-section">
              <summary className="uc__copilot-label" style={{ cursor: 'pointer' }}>Underwriting Guidelines</summary>
              <div className="uc__guidelines-text" style={{ marginTop: 'var(--space-xs)' }}>{uwGuidelines}</div>
            </details>
          )}

          {/* Pipeline stage indicator */}
          {(() => {
            const docs = canvas?.documents || [];
            const hasDocs = docs.length > 0;
            const docProcessing = docs.some((d) => {
              const ds = d.ProcessingStatus || d.processingStatus || '';
              return ds === 'extracting' || ds === 'analyzing' || ds === 'uploading';
            });

            if (hasAssessment && (assessment?.decisionRecommendation || assessment?.DecisionRecommendation)) {
              return null;
            }
            if (hasAssessment) {
              return (
                <div className="uc__copilot-section">
                  <div className="uc__copilot-stage">
                    <span className="uc__copilot-stage-dot uc__copilot-stage-dot--active" />
                    Agent review pending...
                  </div>
                </div>
              );
            }
            if (isScoringInProgress || (subStatus === 'data_extracted' && !hasAssessment)) {
              return (
                <div className="uc__copilot-section">
                  <div className="uc__copilot-stage">
                    <span className="uc__copilot-stage-dot uc__copilot-stage-dot--active" />
                    AI risk scoring in progress...
                  </div>
                </div>
              );
            }
            if (docProcessing) {
              return (
                <div className="uc__copilot-section">
                  <div className="uc__copilot-stage">
                    <span className="uc__copilot-stage-dot uc__copilot-stage-dot--active" />
                    Extracting data from application...
                  </div>
                </div>
              );
            }
            if (subStatus === 'new' && hasDocs) {
              return (
                <div className="uc__copilot-section">
                  <div className="uc__copilot-stage">
                    <span className="uc__copilot-stage-dot uc__copilot-stage-dot--active" />
                    Processing uploaded document...
                  </div>
                </div>
              );
            }
            if (subStatus === 'new') {
              return (
                <div className="uc__copilot-section">
                  <p className="uc__empty-sm">Awaiting application upload. Upload a document to begin AI analysis.</p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>
    </div>
  );
}
